import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Heart, MessageCircle, ShieldOff, ShieldCheck, Star, Mail, Phone, Flag, Activity, CreditCard, RefreshCw, Shield } from 'lucide-react';
import { fetchUserDetails, updateUserStatus } from '../utils/api';

const PLAN_LABELS: Record<string, { label: string; bg: string; color: string }> = {
  chat_week: { label: 'Elite Weekly (₦5,000)', bg: 'rgba(229,57,53,0.12)', color: '#ef4444' },
  chat_2hr: { label: '2-Hour Chat (₦500)', bg: 'rgba(139,92,246,0.12)', color: '#a78bfa' },
  match: { label: 'Instant Match (₦500)', bg: 'rgba(59,130,246,0.12)', color: '#60a5fa' },
  free: { label: 'Free Tier', bg: 'rgba(113,113,122,0.15)', color: '#a1a1aa' },
};

const ACTION_ICON: Record<string, React.ReactNode> = {
  like: <Heart size={13} className="text-red-400" />,
  chat: <MessageCircle size={13} className="text-blue-400" />,
  match: <Heart size={13} className="text-purple-400 fill-purple-400" />,
  edit: <Star size={13} className="text-amber-400" />,
  payment: <CreditCard size={13} className="text-green-400" />,
  security: <ShieldCheck size={13} className="text-emerald-400" />,
};

export const UserProfilePage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userId = id || '';

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [activityLog, setActivityLog] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [actionLoading, setActionLoading] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchUserDetails(userId);
      if (data && data.user) {
        setUser(data.user);
        setActivityLog(data.activityLog || []);
        setPayments(data.payments || []);
      }
    } catch (e) {
      console.error('Error fetching user profile:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const handleModeChanged = () => {
      loadData();
    };
    window.addEventListener('system-mode-changed', handleModeChanged);
    return () => window.removeEventListener('system-mode-changed', handleModeChanged);
  }, [userId]);

  const handleAction = async (action: 'suspend' | 'ban' | 'activate') => {
    if (!user) return;
    setActionLoading(true);
    try {
      await updateUserStatus(user.id, action);
      setUser((prev: any) => ({
        ...prev,
        status: action === 'activate' ? 'active' : action === 'suspend' ? 'suspended' : 'banned',
      }));
    } catch (e) {
      console.error('Failed to update status:', e);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !user) {
    return (
      <div className="py-24 text-center text-zinc-400 max-w-5xl mx-auto">
        <RefreshCw className="animate-spin w-8 h-8 mx-auto mb-3 text-primaryRed" />
        <p className="font-semibold text-white">Loading profile details from Edge D1...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-8 text-center text-zinc-400">
        <p>User profile not found.</p>
        <button onClick={() => navigate('/users')} className="btn-ghost mt-4">Return to Users</button>
      </div>
    );
  }

  const initials = user.name
    ? user.name.split(' ').map((p: string) => p[0]).join('').slice(0, 2).toUpperCase()
    : 'AM';

  const planInfo = PLAN_LABELS[user.plan_type] || {
    label: user.plan_type || 'Elite Weekly (₦5,000)',
    bg: 'rgba(229,57,53,0.12)',
    color: '#ef4444',
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Back Button */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/users')}
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition"
        >
          <ArrowLeft size={16} /> Back to Users Directory
        </button>

        <button
          onClick={loadData}
          disabled={loading}
          className="btn-ghost text-xs flex items-center gap-1.5 py-1.5 px-3"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh Profile
        </button>
      </div>

      {/* Main Profile Header Card */}
      <div className="glass rounded-2xl overflow-hidden border border-darkBorder shadow-2xl bg-gradient-to-b from-zinc-900/90 to-zinc-950/90">
        {/* Banner with gradient & Trust badge */}
        <div className="h-32 relative bg-gradient-to-r from-red-950/40 via-purple-950/40 to-zinc-900 border-b border-darkBorder">
          <div className="absolute top-4 right-4 flex items-center gap-2 flex-wrap">
            {Boolean(user.is_kyc_verified) && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-green-500/10 border border-green-500/30 text-green-400 backdrop-blur-md">
                <ShieldCheck size={14} /> Dojah Biometric KYC (Level {user.kyc_level || 1})
              </div>
            )}
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-blue-500/10 border border-blue-500/30 text-blue-400 backdrop-blur-md">
              <Shield size={14} /> Trust Score: {user.trust_score ?? 0}/100
            </div>
          </div>
        </div>

        <div className="px-6 pb-6">
          {/* Avatar and Action Controls */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between -mt-14 mb-4 gap-4">
            <div
              className="w-24 h-24 rounded-2xl flex items-center justify-center text-2xl font-black text-white shadow-xl ring-4 ring-zinc-950"
              style={{ background: 'linear-gradient(135deg,#e53935,#8b5cf6)' }}
            >
              {initials}
            </div>

            {/* Status Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              {user.status !== 'suspended' && (
                <button
                  disabled={actionLoading}
                  className="btn-ghost flex items-center gap-1.5 text-xs text-amber-400 border-amber-500/30 hover:bg-amber-500/10"
                  onClick={() => handleAction('suspend')}
                >
                  <ShieldOff size={14} /> Suspend Account
                </button>
              )}
              {user.status !== 'active' && (
                <button
                  disabled={actionLoading}
                  className="btn-ghost flex items-center gap-1.5 text-xs text-green-400 border-green-500/30 hover:bg-green-500/10"
                  onClick={() => handleAction('activate')}
                >
                  <ShieldCheck size={14} /> Activate Account
                </button>
              )}
              {user.status !== 'banned' && (
                <button
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 text-xs h-9 px-4 rounded-xl font-bold bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500 hover:text-white transition"
                  onClick={() => handleAction('ban')}
                >
                  <Flag size={14} /> Ban User
                </button>
              )}
            </div>
          </div>

          {/* User Details */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h1 className="font-display font-extrabold text-2xl text-white">
                {user.name}{user.age ? `, ${user.age}` : ''}
              </h1>
              <span
                className="text-xs font-bold px-2.5 py-1 rounded-lg"
                style={{ background: planInfo.bg, color: planInfo.color }}
              >
                {planInfo.label}
              </span>
              <span className={`badge ${user.status === 'active' ? 'badge-green' : user.status === 'suspended' ? 'badge-amber' : 'badge-red'} capitalize text-xs`}>
                {user.status}
              </span>
            </div>

            <p className="text-sm text-zinc-300 max-w-2xl leading-relaxed mb-4">
              {user.bio || 'No bio written yet.'}
            </p>

            <div className="flex flex-wrap gap-5 text-xs text-zinc-400">
              <span className="flex items-center gap-1.5"><Mail size={14} className="text-zinc-500" />{user.email}</span>
              <span className="flex items-center gap-1.5"><Phone size={14} className="text-zinc-500" />{user.phone || 'No phone recorded'}</span>
              <span className="flex items-center gap-1.5"><MapPin size={14} className="text-zinc-500" />{user.location || 'Lagos, NG'}</span>
              <span className="flex items-center gap-1.5"><Calendar size={14} className="text-zinc-500" />Joined {user.created_at ? user.created_at.slice(0, 10) : user.joined || 'Recently'}</span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                Active on platform
              </span>
            </div>
          </div>

          {/* Metrics Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-darkBorder">
            <div className="bg-zinc-900/60 border border-darkBorder rounded-xl p-4 text-center">
              <Heart size={20} className="mx-auto mb-1.5 text-primaryRed" />
              <p className="font-bold text-2xl font-display text-white">{(user.matches || 0).toLocaleString()}</p>
              <p className="text-xs text-zinc-400 mt-0.5">Mutual Matches</p>
            </div>
            <div className="bg-zinc-900/60 border border-darkBorder rounded-xl p-4 text-center">
              <Star size={20} className="mx-auto mb-1.5 text-amber-400" />
              <p className="font-bold text-2xl font-display text-white">{(user.likes || 0).toLocaleString()}</p>
              <p className="text-xs text-zinc-400 mt-0.5">Likes Sent</p>
            </div>
            <div className="bg-zinc-900/60 border border-darkBorder rounded-xl p-4 text-center">
              <MessageCircle size={20} className="mx-auto mb-1.5 text-purple-400" />
              <p className="font-bold text-2xl font-display text-white">{(user.messages || 0).toLocaleString()}</p>
              <p className="text-xs text-zinc-400 mt-0.5">Encrypted Messages</p>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Log & Naira Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Log */}
        <div className="glass rounded-2xl overflow-hidden border border-darkBorder">
          <div className="flex items-center justify-between px-5 py-4 border-b border-darkBorder bg-zinc-900/40">
            <div className="flex items-center gap-2">
              <Activity size={16} className="text-zinc-400" />
              <h2 className="font-bold text-sm text-white">Real-Time Activity Audit</h2>
            </div>
            <span className="text-xs text-zinc-500">Live Edge Feed</span>
          </div>
          <div className="divide-y divide-darkBorder">
            {activityLog.length === 0 ? (
              <p className="p-6 text-xs text-center text-zinc-500">No recent activity logged</p>
            ) : (
              activityLog.map((log, i) => (
                <div key={i} className="flex items-start gap-3.5 px-5 py-3.5 hover:bg-white/[0.02] transition">
                  <div className="w-7 h-7 rounded-lg bg-zinc-800/80 flex items-center justify-center shrink-0 mt-0.5 border border-darkBorder">
                    {ACTION_ICON[log.type] || <Activity size={13} className="text-zinc-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-zinc-200">{log.action}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">{log.time}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* SquadCo Payment History */}
        <div className="glass rounded-2xl overflow-hidden border border-darkBorder">
          <div className="flex items-center justify-between px-5 py-4 border-b border-darkBorder bg-zinc-900/40">
            <div className="flex items-center gap-2">
              <CreditCard size={16} className="text-zinc-400" />
              <h2 className="font-bold text-sm text-white">SquadCo Transactions (₦ NGN)</h2>
            </div>
            <span className="badge badge-green text-xs">Squad API</span>
          </div>
          <div className="divide-y divide-darkBorder">
            {payments.length === 0 ? (
              <p className="p-6 text-xs text-center text-zinc-500">No payment history found</p>
            ) : (
              payments.map((p, i) => (
                <div key={i} className="flex items-center justify-between gap-4 px-5 py-3.5 hover:bg-white/[0.02] transition">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-white truncate">{p.desc}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">{p.date}</p>
                  </div>
                  <p className="font-mono font-bold text-sm text-green-400">{p.amount}</p>
                  <span className={`badge ${p.status === 'success' ? 'badge-green' : 'badge-red'} text-xs uppercase`}>
                    {p.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
